import { useMediaQuery, useTheme } from "@mui/material";
import { TPosition } from "../models/TPosition";
import { TStyle } from "../models/TStyle";

const calculatePositionStyle = (
    style: TStyle,
    position: TPosition,
    isScreenSmall: boolean
) => {
    if (!style)
        return {
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
        };

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Card dimensions will be estimated since style contains anchor element properties

    if (isScreenSmall) {
        // For small screens, use full width cards positioned above or below the anchor element
        const fullWidth = viewportWidth - 20; // Full width with 10px padding on each side
        const cardHeight = 200; // Estimated card height (you may need to adjust this)
        let top;
        let bottom;

        // Calculate available space above and below the anchor element
        const spaceAbove = style.top;
        const spaceBelow = viewportHeight - (style.top + style.height);

        // Determine best vertical position (above or below the anchor)
        if (position === "top" || position === "left" || position === "right") {
            // Try to position above the anchor first
            if (spaceAbove >= cardHeight + 10) {
                top = style.top - cardHeight - 10;
            } else if (spaceBelow >= cardHeight + 10) {
                // Position below if not enough space above
                top = style.top + style.height + 10;
            } else {
                // If card is bigger than available space, prioritize visibility
                if (cardHeight > viewportHeight - 20) {
                    top = 0; // Bound to top of screen for maximum visibility
                } else {
                    // If neither has enough space, position above but adjust to fit
                    top = Math.max(10, style.top - cardHeight - 10);
                }
            }
        } else if (position === "bottom") {
            // Try to position below the anchor first
            if (spaceBelow >= cardHeight + 10) {
                top = style.top + style.height + 10;
            } else if (spaceAbove >= cardHeight + 10) {
                // Position above if not enough space below
                top = style.top - cardHeight - 10;
            } else {
                // If card is bigger than available space, prioritize visibility
                if (cardHeight > viewportHeight - 20) {
                    bottom = 0; // Bound to bottom of screen for maximum visibility
                } else {
                    // If neither has enough space, position below but adjust to fit
                    top = Math.min(
                        viewportHeight - cardHeight - 10,
                        style.top + style.height + 10
                    );
                }
            }
        }

        // Return positioning based on whether we're using top or bottom
        const baseStyle = {
            left: `10px`, // Always 10px from left edge
            width: `${fullWidth}px`,
            maxWidth: `${viewportWidth - 20}px`,
            maxHeight: `${viewportHeight - 20}px`,
        };

        if (bottom !== undefined) {
            return {
                ...baseStyle,
                bottom: `${bottom}px`,
            };
        } else {
            return {
                ...baseStyle,
                top: `${top}px`,
            };
        }
    }

    // Original logic for larger screens
    if (position === "top") {
        return {
            top: `${style?.top - 10}px`,
            left: `${style?.left + style?.width / 2}px`,
            transform: "translate(-50%, -100%)",
        };
    }
    if (position === "bottom") {
        return {
            top: `${style?.top + style?.height + 10}px`,
            left: `${style?.left + style?.width / 2}px`,
            transform: "translateX(-50%)",
        };
    }
    if (position === "left") {
        return {
            top: `${style?.top + style?.height / 2}px`,
            left: `${style?.left}px`,
            transform: "translate(calc(-100% - 10px), -50%)",
        };
    }
    if (position === "right") {
        return {
            top: `${style?.top + style?.height / 2}px`,
            left: `${style?.left + style?.width}px`,
            transform: "translateY( -50%)",
        };
    }
};

export default function useCalculatePositionStyle() {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    return (style: TStyle, position: TPosition) => {
        return calculatePositionStyle(style, position, isSmallScreen);
    };
}
